import { create } from "zustand"

import "../graphql/glTransactionRowList.query"

import { GlTransactionDateType } from "../types/searchParams"

import {
  type GlTransactionPostedByFilter,
  type GlTransactionRowSortField,
  type GlTransactionRowType,
  type GlTransactionStatus,
  ListGlTransactionRowsDocument,
  type ListGlTransactionRowsQuery,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

type TransactionRowListResult = ListGlTransactionRowsQuery["glTransaction"]["rowList"]
type TransactionRow = NonNullable<
  Extract<TransactionRowListResult, { __typename: "GlTransactionRowListSuccess" }>["glTransactionRows"][number]
>

export type TransactionsSearchFilters = SearchFilters & {
  field?: GlTransactionRowSortField
  order?: SortingOrder
  transactionType?: GlTransactionRowType
  statuses?: GlTransactionStatus[]
  postedByType?: GlTransactionPostedByFilter
  dateType?: GlTransactionDateType
  dateFrom?: string
  dateTo?: string
}

const deduplicateRows = (newRows: TransactionRow[], existing: TransactionRow[]): TransactionRow[] => {
  const seen = new Set(existing.map((row) => row.glTransactionRowId))
  return [...existing, ...newRows.filter((row) => !seen.has(row.glTransactionRowId))]
}

export const useTransactionsSearchStore = create(
  createSearchStore<TransactionRow, TransactionsSearchFilters, ListGlTransactionRowsQuery>({
    documentNode: ListGlTransactionRowsDocument,
    deduplicateData: deduplicateRows,

    getQueryInput: (filters: TransactionsSearchFilters, offset: number, limit: number) => {
      const { field, order, transactionType, statuses, postedByType, dateType, dateFrom, dateTo, query } = filters

      const dateRangeFilter = dateFrom && dateTo ? { from: dateFrom, to: dateTo } : undefined

      return {
        query: query || undefined,
        offset,
        limit,
        filter: {
          type: transactionType ?? undefined,
          status: statuses?.length ? statuses : undefined,
          postedBy: postedByType ?? undefined,
          createdDate: dateType === GlTransactionDateType.CreatedDate ? dateRangeFilter : undefined,
          transactionDate: dateType === GlTransactionDateType.TransactionDate ? dateRangeFilter : undefined,
        },
        sort: field && order ? { field, order } : undefined,
      }
    },

    getQueryVariables: (queryInput) => ({ input: queryInput }),

    extractDataFromResponse: (data) => {
      const result = data.glTransaction.rowList
      if (result.__typename !== "GlTransactionRowListSuccess") {
        return {
          __typename: result.__typename,
          data: [],
          error: result.__typename === "GlTransactionFailure" ? result.error : undefined,
        }
      }
      return {
        __typename: result.__typename,
        data: result.glTransactionRows.filter((row): row is TransactionRow => row !== null),
      }
    },
  })
)
